import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useRobotsMeta, markPrerenderReady } from "@/lib/seo";
import {
  Copy,
  CheckCircle2,
  Search,
  Share2,
  Instagram,
  Facebook,
  Youtube,
  ArrowUpDown,
  MapPin,
  Globe,
  Building2,
  MessageCircle,
  Tag,
  Sparkles,
  BadgeCheck,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Star,
  ShoppingCart,
} from "lucide-react";
import { fetchStoreWithStock } from "@/lib/storeFetch";
import { currencySymbol } from "@/lib/currency";
import { useStorefrontCart } from "@/lib/storefrontCart";
import StorefrontCartButton from "@/components/StorefrontCartButton";

const SORTS = {
  default: { label: "Featured", fn: null },
  price_asc: { label: "Price: Low to High", fn: (a, b) => Number(a.price) - Number(b.price) },
  price_desc: { label: "Price: High to Low", fn: (a, b) => Number(b.price) - Number(a.price) },
};

// Visual tier escalation: starter (unchanged) -> growth (soft pastel card) ->
// pro (bold gradient card + verified badge). Purely presentational — driven
// entirely by store.plan from the cached payload, no new merchant input needed.
const TIER_STYLES = {
  starter: {
    bannerClass: "",
    eyebrowClass: "text-hotpink",
    headingClass: "text-ink",
    taglineClass: "text-ink/70",
    descClass: "text-ink/60",
    logoWrapClass: "",
    badge: false,
    badgeClass: "",
    outlineBtnClass: "inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-4 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5",
    ctaBtnClass: "inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95",
    pageBgStyle: {},
  },
  growth: {
    bannerClass: "overflow-hidden rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8",
    eyebrowClass: "text-hotpink",
    headingClass: "text-ink",
    taglineClass: "text-ink/70",
    descClass: "text-ink/60",
    logoWrapClass: "ring-2 ring-ink/10",
    badge: false,
    badgeClass: "",
    outlineBtnClass: "inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-4 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5",
    ctaBtnClass: "inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95",
    pageBgStyle: {},
  },
  pro: {
    // White background removed — banner is now just an outlined card that
    // lets the page's cream background show through, matching starter/growth.
    // Verification still reads through the amber border + badge.
    bannerClass: "overflow-hidden rounded-[2rem] border border-amber-200 p-6 md:p-8",
    eyebrowClass: "text-amber-700",
    headingClass: "text-ink",
    taglineClass: "text-ink/70",
    descClass: "text-ink/60",
    logoWrapClass: "ring-2 ring-amber-200 bg-white p-0.5",
    badge: true,
    badgeClass: "text-amber-500",
    outlineBtnClass: "inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-4 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5",
    ctaBtnClass: "inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95",
    // Removed the whole-page radial-gradient glow — it was stacking a color
    // wash under the banner and hurting text contrast further.
    pageBgStyle: {},
  },
};

// The cache bakes in the rule's raw expires_at rather than a pre-computed
// "is it still active" flag — that decision is made HERE, live, on every
// page load. More accurate than checking at rebuild time, and free: it's
// just a date comparison already sitting in memory.
function isDiscountStillActive(rule) {
  if (!rule.expires_at) return true;
  return new Date(rule.expires_at) > new Date();
}

function describeDiscount(rule, currency) {
  const symbol = currencySymbol(currency);
  const value =
    rule.discount_kind === "percent"
      ? `${Number(rule.discount_value)}% off`
      : rule.discount_kind === "flat"
      ? `${symbol}${Number(rule.discount_value)} off`
      : "Free shipping";
  const min = rule.min_order_value != null ? ` orders over ${symbol}${Number(rule.min_order_value)}` : "";
  return `${value}${min}`;
}

function formatExpiry(expiresAt) {
  if (!expiresAt) return null;
  const d = new Date(expiresAt);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// Same reasoning as isDiscountStillActive: computed live in the browser on
// every page load, not baked into the cache at rebuild time. A product
// stays "New" for exactly 14 days regardless of when the cache last rebuilt.
const NEW_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
function isRecentlyAdded(product) {
  if (!product.created_at) return false;
  return Date.now() - new Date(product.created_at).getTime() < NEW_WINDOW_MS;
}

// Catches simple typos ("shrt" → "shirt", "jaket" → "jacket") by checking
// whether a token is within edit-distance 1 of a word in the haystack —
// one insertion, deletion, or substitution. Only applied to tokens 3+
// chars long, to avoid noisy matches on short words like "of" or "a".
function withinEditDistance1(a, b) {
  if (a === b) return true;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  let i = 0, j = 0, diff = 0;
  while (i < la && j < lb) {
    if (a[i] === b[j]) { i++; j++; continue; }
    diff++;
    if (diff > 1) return false;
    if (la === lb) { i++; j++; }
    else if (la > lb) { i++; }
    else { j++; }
  }
  if (i < la || j < lb) diff++;
  return diff <= 1;
}

/**
 * PublicStorefront.jsx
 * ------------------------------------------------------------------
 * Shareable, NOT search-engine-indexable catalog page. Route: /store/:username
 *
 * Loads once from GET /public/store?u=:username — one cached JSON payload,
 * one indexed Postgres read server-side regardless of visit count.
 * Search/sort run entirely client-side against that one payload.
 *
 * Tiered content, all driven by which fields are present in the payload
 * (the cache-build query only fills in fields the merchant's plan allows —
 * this component doesn't need to know about plans at all, it just renders
 * whatever's there):
 *   - Starter+: address
 *   - Growth+: social links, active discounts, "New" badges
 *   - Pro: logo/tagline/website/registration/phone, multi-photo gallery
 * ------------------------------------------------------------------
 */
export default function PublicStorefront() {
  const { username } = useParams();
  const [store, setStore] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not_found | error
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("default");
  const [selectedCategory, setSelectedCategory] = useState(null); // null = "All"
  const [stockMap, setStockMap] = useState({});
  const [shownCount, setShownCount] = useState(24);
  const cart = useStorefrontCart(username);

  useEffect(() => {
    setShownCount(24);
  }, [query, sortKey, selectedCategory]);

  // The listing/search page itself stays noindex on every plan — it's
  // thin, search-widget content. Only individual product pages
  // (PublicProductPage.jsx) become indexable, and only for Pro merchants.
  useRobotsMeta("noindex, nofollow");

  useEffect(() => {
    let cancelled = false;
    fetchStoreWithStock(username)
      .then(({ store: storeData, stockMap: stockData }) => {
        if (cancelled) return;
        setStore(storeData);
        setStockMap(stockData);
        setStatus("ok");
      })
      .catch((e) => {
        if (cancelled) return;
        setStatus(e?.response?.status === 404 ? "not_found" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [username]);

  useEffect(() => {
    if (status !== "loading") markPrerenderReady();
  }, [status]);

  // Stock and the catalog version are loaded together with the store payload
  // (see fetchStoreWithStock in lib/storeFetch.js).

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 3200);
  };

  // Starter+ — category is only present in the cached payload for those
  // plans (see cache-build query), so this is naturally empty on Free.
  // Case/whitespace shouldn't create separate chips ("Tops" and "tops" are the
  // same category) — dedupe on a normalized key, but keep whichever casing was
  // typed first as the display label so chips still look intentional.
  const categories = useMemo(() => {
    if (!store) return [];
    const seen = new Map(); // normalized key -> first-seen display label
    store.products.forEach((p) => {
      if (!p.category) return;
      const label = p.category.trim();
      const key = label.toLowerCase();
      if (key && !seen.has(key)) seen.set(key, label);
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [store]);

  const visibleProducts = useMemo(() => {
    if (!store) return [];
    // Tokenized AND-match across name + description + category, not a single
    // substring of the whole phrase — "cotton shirt" now matches a product
    // titled "Shirt" whose description mentions "100% cotton", in either order.
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const isSearching = tokens.length > 0;

    let scored = store.products.map((p) => {
      const name = p.name.toLowerCase();
      const desc = (p.description || "").toLowerCase();
      const cat = (p.category || "").toLowerCase();
      if (!isSearching) return { p, score: 0, matches: true };
      const haystack = `${name} ${desc} ${cat}`;
      const haystackWords = haystack.split(/[^a-z0-9]+/).filter(Boolean);

      // Exact substring hit first (cheap, catches plurals/partial words too);
      // only fall back to a fuzzy word check when that misses.
      const tokenHit = (t) => {
        if (haystack.includes(t)) return { hit: true, fuzzy: false };
        if (t.length >= 3 && haystackWords.some((w) => w.length >= 3 && withinEditDistance1(t, w))) {
          return { hit: true, fuzzy: true };
        }
        return { hit: false, fuzzy: false };
      };

      const results = tokens.map(tokenHit);
      const matches = results.every((r) => r.hit);
      const score = tokens.reduce((sum, t, idx) => {
        if (!results[idx].hit) return sum;
        if (results[idx].fuzzy) return sum + 0.5; // fuzzy hits rank below exact ones
        let s = 0;
        if (name.includes(t)) s += 3;
        if (cat.includes(t)) s += 2;
        if (desc.includes(t)) s += 1;
        return sum + s;
      }, 0);
      return { p, score, matches };
    });

    let list = scored.filter((r) => r.matches);
    if (selectedCategory) {
      const key = selectedCategory.trim().toLowerCase();
      list = list.filter((r) => (r.p.category || "").trim().toLowerCase() === key);
    }

    const sortFn = SORTS[sortKey]?.fn;
    if (sortFn) {
      // An explicit sort choice (price, etc.) always wins over relevance.
      list = [...list].sort((a, b) => sortFn(a.p, b.p));
    } else if (isSearching) {
      list = [...list].sort((a, b) => b.score - a.score);
    } else {
      // Default view, no search: bestsellers first (Pro-only, max 3), then
      // everything else stays in whatever order the cache returned.
      list = [...list].sort((a, b) => (b.p.is_bestseller ? 1 : 0) - (a.p.is_bestseller ? 1 : 0));
    }
    return list.map((r) => r.p);
  }, [store, query, sortKey, selectedCategory]);

  const activeDiscounts = useMemo(
    () => (store?.discounts || []).filter(isDiscountStillActive),
    [store]
  );

  const copyDetails = async (product) => {
    try {
      await navigator.clipboard.writeText(product.name);
      flash("Copied! Paste it when you message us.");
    } catch {
      flash("Couldn't copy — try again.");
    }
  };

  const availableQtyFor = (productId, size) => {
    const stock = stockMap[productId];
    if (!stock) return null; // unknown — don't block, server still validates at checkout
    const raw = size ? stock.stock_by_size?.[size] : stock.overall_qty;
    return raw === undefined || raw === null ? null : Number(raw);
  };

  const addToCart = (product, size) => {
    const available = availableQtyFor(product.id, size);
    if (available !== null) {
      const existing = cart.items.find(
        (l) => l.catalog_item_id === product.id && (l.size || null) === (size || null)
      );
      const currentQty = existing ? existing.quantity : 0;
      if (currentQty >= available) {
        flash(available <= 0 ? "This item is out of stock." : `Only ${available} left — that's already in your cart.`);
        return;
      }
    }
    cart.addItem({
      catalog_item_id: product.id,
      name: product.name,
      price: Number(product.price),
      image_url: product.image_url,
      size: size || null,
      quantity: 1,
      is_digital: !!product.is_digital,
    });
    flash("Added to cart!");
  };

  const openInstagram = () => {
    window.open(`https://www.instagram.com/${username}/`, "_blank", "noopener,noreferrer");
  };

  const shareLink = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: `@${username}'s catalog`, url });
      } catch {
        // user cancelled the share sheet
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        flash("Link copied!");
      } catch {
        flash("Couldn't copy the link.");
      }
    }
  };

  const branding = store?.branding || null;
  const social = store?.social_links || null;
  const hasSocial = social && (social.instagram || social.facebook || social.youtube);
  const previewProduct = store?.products?.[0];
  const tier = TIER_STYLES[store?.plan] || TIER_STYLES.starter;

  return (
        <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16" style={tier.pageBgStyle}>
      {status === "ok" && store && (
        <Helmet>
          <title>{`@${store.ig_username}'s catalog`}</title>
          <link rel="canonical" href={typeof window !== "undefined" ? window.location.href.split("?")[0] : ""} />
          <meta property="og:url" content={typeof window !== "undefined" ? window.location.href.split("?")[0] : ""} />
          <meta property="og:title" content={`@${store.ig_username}'s catalog`} />
          <meta
            property="og:description"
            content={`Browse ${store.products.length} product${store.products.length === 1 ? "" : "s"} from @${store.ig_username} on CheckoutDM.`}
          />
          {(branding?.logo_url || previewProduct?.image_url) && (
            <meta property="og:image" content={branding?.logo_url || previewProduct.image_url} />
          )}
          <meta property="og:type" content="website" />
        </Helmet>
      )}

      <div className="mx-auto max-w-5xl">
        {notice && (
          <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-cream shadow-lg">
            {notice}
          </div>
        )}

        {status === "loading" && (
          <div className="grid h-64 place-items-center text-sm text-ink/50">Loading…</div>
        )}

        {status === "not_found" && (
          <div className="grid h-64 place-items-center text-center">
            <div>
              <p className="text-2xl font-black text-ink">Store not found</p>
              <p className="mt-2 text-sm text-ink/50">
                This link may be wrong, or the merchant hasn't turned on their public catalog.
              </p>
              <a
                href="/"
                className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85"
              >
                Visit CheckoutDM.com <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="grid h-64 place-items-center text-center">
            <div>
              <p className="text-sm text-ink/50">Something went wrong loading this store. Try refreshing.</p>
              <a
                href="/"
                className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85"
              >
                Visit CheckoutDM.com <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        )}

        {status === "ok" && store && (
          <>
            {/* Header — banner escalates by plan (starter: none, growth: pastel card, pro: outlined card + verified badge) */}
            <div className={tier.bannerClass}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                  {branding?.logo_url && (
                    <div className={`flex-none rounded-2xl ${tier.logoWrapClass}`}>
                      <img
                        src={branding.logo_url}
                        alt=""
                        className="h-20 w-20 rounded-2xl object-cover"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    {/* IG handle — bumped from text-xs to text-base and badge
                        icon from 14 to 18 so it reads as a proper heading,
                        not fine print. min-w-0 here (and on the flex row
                        above) stops a long @username from overflowing past
                        the card's padding/border — it wraps to a second
                        line instead on narrow screens. */}
                    <p className={`flex flex-wrap items-center gap-1.5 text-base font-semibold uppercase tracking-[0.18em] ${tier.eyebrowClass}`}>
                      <span className="break-words">@{store.ig_username}</span>
                      {tier.badge && <BadgeCheck size={18} className={`flex-none ${tier.badgeClass}`} />}
                    </p>
                    {/* Tagline now sits directly under the IG handle, ahead of
                        the "Browse the catalog." heading. */}
                    {branding?.tagline && (
                      <p className={`mt-1 text-sm font-semibold ${tier.taglineClass}`}>{branding.tagline}</p>
                    )}
                    <h1 className={`mt-2 text-4xl font-black tracking-tight md:text-5xl ${tier.headingClass}`}>
                      Browse the catalog.
                    </h1>
                    <p className={`mt-2 max-w-xl ${tier.descClass}`}>
                      Copy a product's details, then message @{store.ig_username} on Instagram to order. You can even order directly from this storefront.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <StorefrontCartButton username={username} currency={store.currency} cart={cart} stockMap={stockMap} />
                  <button type="button" onClick={shareLink} className={tier.outlineBtnClass}>
                    <Share2 size={14} /> Share
                  </button>
                  <button type="button" onClick={openInstagram} className={tier.ctaBtnClass}>
                    <Instagram size={14} /> Message us
                  </button>
                </div>
              </div>
            </div>

          {/* Address — its own line, Starter+ */}
            {store.address && (
              <div className="mt-5 flex items-center gap-1.5 text-sm text-ink/60">
                <MapPin size={13} className="flex-none text-ink/40" /> {store.address}
              </div>
            )}

            {/* Contact strip — phone/WhatsApp (Pro), website (Pro), social (Growth+), on their own line */}
            {(branding?.phone || branding?.website_url || hasSocial) && (
              <div className={`flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink/60 ${store.address ? "mt-2" : "mt-5"}`}>
                {branding?.phone && (
                  <a
                    href={`https://wa.me/${branding.phone.replace(/[^\d]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-semibold text-ink hover:text-hotpink"
                  >
                    <MessageCircle size={13} className="text-ink/40" /> WhatsApp
                  </a>
                )}
                {branding?.website_url && (
                  <a
                    href={branding.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-semibold text-ink hover:text-hotpink"
                  >
                    <Globe size={13} className="text-ink/40" /> Visit website
                  </a>
                )}
                {hasSocial && (
                  <span className="inline-flex items-center gap-3.5">
                    {social.instagram && (
                      <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="text-ink/60 hover:text-hotpink">
                        <Instagram size={18} />
                      </a>
                    )}
                    {social.facebook && (
                      <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="text-ink/60 hover:text-hotpink">
                        <Facebook size={18} />
                      </a>
                    )}
                    {social.youtube && (
                      <a href={social.youtube} target="_blank" rel="noopener noreferrer" className="text-ink/60 hover:text-hotpink">
                        <Youtube size={18} />
                      </a>
                    )}
                  </span>
                )}
              </div>
            )}

            {/* Active discounts — Growth+, live expiry check. Capped at 3 by
                the merchant (the "featured" toggle), so a small grid fits
                them all without the old stack of full-width bars. */}
            {activeDiscounts.length > 0 && (
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {activeDiscounts.map((d) => (
                  <DiscountCard key={d.id} discount={d} currency={store.currency} />
                ))}
              </div>
            )}

            {categories.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCategory(null)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                    selectedCategory === null
                      ? "bg-ink text-cream"
                      : "border border-ink/15 bg-white text-ink/70 hover:bg-ink/5"
                  }`}
                >
                  All
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSelectedCategory(c)}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                      selectedCategory === c
                        ? "bg-ink text-cream"
                        : "border border-ink/15 bg-white text-ink/70 hover:bg-ink/5"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {store.products.length > 0 && (
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search products…"
                    className="w-full rounded-2xl border border-ink/15 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-hotpink"
                  />
                </div>
                <div className="relative">
                  <ArrowUpDown size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
                  <select
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value)}
                    className="w-full appearance-none rounded-2xl border border-ink/15 bg-white py-2.5 pl-10 pr-8 text-sm outline-none focus:border-hotpink sm:w-56"
                  >
                    {Object.entries(SORTS).map(([key, { label }]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {store.products.length === 0 ? (
              <p className="mt-10 rounded-2xl border border-dashed border-ink/15 bg-ink/[0.02] p-8 text-center text-sm text-ink/50">
                No products listed yet — check back soon.
              </p>
            ) : visibleProducts.length === 0 ? (
              <p className="mt-10 rounded-2xl border border-dashed border-ink/15 bg-ink/[0.02] p-8 text-center text-sm text-ink/50">
                {query ? `No products match "${query}".` : `No products in ${selectedCategory}.`}
              </p>
            ) : (
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {visibleProducts.slice(0, shownCount).map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    stock={stockMap[p.id]}
                    currency={store.currency}
                    username={username}
                    onCopy={() => copyDetails(p)}
                    onAddToCart={(size) => addToCart(p, size)}
                  />
                ))}
                {visibleProducts.length > shownCount && (
                  <div className="col-span-full flex justify-center pt-2">
                    <button
                      type="button"
                      onClick={() => setShownCount((n) => n + 24)}
                      className="rounded-full border border-ink/15 bg-white px-6 py-2.5 text-sm font-semibold hover:border-ink/30"
                    >
                      Show more ({visibleProducts.length - shownCount} left)
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Business registration number — Pro, small print, bottom of page */}
            {branding?.business_registration_number && (
              <p className="mt-10 text-center text-xs text-ink/30">
                Reg. No: {branding.business_registration_number}
              </p>
            )}

            {/* CheckoutDM branding — Creator only. Starter+ show none of this. */}
            {store.plan === "creator" && (
              <p className="mt-10 text-center text-xs text-ink/30">
                Powered by{" "}
                <a
                  href="https://checkoutdm.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-ink/40 hover:text-hotpink"
                >
                  CheckoutDM
                </a>
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// One coupon as a compact, self-contained card — icon + description on
// top, a copyable code chip (manual codes) or an "applied automatically"
// label on the bottom. Mirrors ProductCard's own copy-button pattern.
function DiscountCard({ discount, currency }) {
  const expiry = formatExpiry(discount.expires_at);
  const hasCode = discount.rule_type === "manual_code" && discount.code;
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    if (!hasCode) return;
    try {
      await navigator.clipboard.writeText(discount.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // clipboard write failed — button just won't show the "copied" state
    }
  };

  return (
    <div className="flex flex-col justify-between gap-3 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-4">
      <div className="flex items-start gap-2.5">
        <span className="grid h-8 w-8 flex-none place-items-center rounded-xl bg-hotpink/10 text-hotpink">
          <Tag size={15} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold leading-snug text-ink">{describeDiscount(discount, currency)}</p>
          {expiry && <p className="mt-0.5 text-xs text-ink/50">Ends {expiry}</p>}
        </div>
      </div>
      {hasCode ? (
        <button
          type="button"
          onClick={copyCode}
          className="flex items-center justify-between gap-2 rounded-xl border border-dashed border-hotpink/40 bg-white px-3 py-2 text-xs font-bold tracking-wide text-hotpink hover:bg-hotpink/5"
        >
          <span className="truncate">{discount.code}</span>
          {copied ? <CheckCircle2 size={13} className="flex-none" /> : <Copy size={13} className="flex-none" />}
        </button>
      ) : (
        <span className="text-xs font-semibold uppercase tracking-wide text-ink/40">Applied automatically</span>
      )}
    </div>
  );
}

// Only https links are allowed for the merchant's Buy / affiliate link (same rule as the Instagram DM card).
function safeHttpsUrl(u) {
  return typeof u === "string" && /^https:\/\/\S+$/i.test(u.trim()) ? u.trim() : null;
}

function ProductCard({ product, stock, currency, username, onCopy, onAddToCart }) {
  const symbol = currencySymbol(currency);
  const [copied, setCopied] = useState(false);
  // Buy / affiliate link (set in Catalog). "link_only" = no Add button, customer goes straight to the merchant's link.
  const buyUrl = safeHttpsUrl(product.external_url);
  const buyLabel = (product.external_label || "").trim() || "🛒 Shop now";
  const isLinkOnly = !!buyUrl && product.dm_action === "link_only";
  const sizes = isLinkOnly ? [] : (product.sizes || "").split(",").map((s) => s.trim()).filter(Boolean);
  const hasSizes = sizes.length > 0;
  const stockBySize = stock?.stock_by_size || {};
  const isSizeOutOfStock = (s) => {
    const q = stockBySize[s];
    return q !== undefined && q !== null && Number(q) <= 0;
  };
  const availableSizes = hasSizes ? sizes.filter((s) => !isSizeOutOfStock(s)) : sizes;
  const overallOutOfStock =
    !hasSizes && stock && stock.overall_qty !== undefined && stock.overall_qty !== null && Number(stock.overall_qty) <= 0;
  const allSizesOutOfStock = hasSizes && stock && availableSizes.length === 0;
  const isOutOfStock = !isLinkOnly && (overallOutOfStock || allSizesOutOfStock);
  const [selectedSize, setSelectedSize] = useState(sizes[0] || null);
  useEffect(() => {
    if (hasSizes && selectedSize && isSizeOutOfStock(selectedSize) && availableSizes.length > 0) {
      setSelectedSize(availableSizes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock]);
  const gallery = [product.image_url, ...(product.additional_image_urls || [])].filter(Boolean);
  const [activeImage, setActiveImage] = useState(0);
  // Older products created before the slug backfill may not have one yet —
  // fall back to rendering a non-clickable image rather than a dead link.
  const productHref = product.slug ? `/store/${username}/p/${product.slug}` : null;

  const handleCopy = () => {
    onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white">
      <div className="relative w-full bg-ink/5 h-80 sm:h-64 lg:h-72">
        {gallery.length > 0 ? (
          productHref ? (
            <Link to={productHref}>
              <img
                src={gallery[activeImage]}
                alt={product.name}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </Link>
          ) : (
            <img
              src={gallery[activeImage]}
              alt={product.name}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          )
        ) : (
          <div className="grid h-full w-full place-items-center text-xs text-ink/30">No image</div>
        )}
        {isRecentlyAdded(product) && (
          <span className="absolute left-2 top-2 rounded-full bg-mint px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
            <Sparkles size={10} className="mr-1 inline" /> New
          </span>
        )}
        {product.is_bestseller && (
          <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
            <Star size={10} className="fill-ink" /> Bestseller
          </span>
        )}
        {isOutOfStock && (
          <div className="absolute inset-0 grid place-items-center bg-ink/40">
            <span className="rounded-full bg-ink px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
              Out of stock
            </span>
          </div>
        )}
        {/* Prev/next arrows — always visible (not hover-only), since a
            hover-only control is invisible on touch devices with no
            equivalent gesture. Stops event bubbling so a tap here doesn't
            trigger any parent click handlers. Only rendered when there's
            actually more than one photo to move between. */}
        {gallery.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImage((i) => (i - 1 + gallery.length) % gallery.length);
              }}
              aria-label="Previous photo"
              className="absolute left-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-full bg-ink/50 text-white backdrop-blur-sm hover:bg-ink/70"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImage((i) => (i + 1) % gallery.length);
              }}
              aria-label="Next photo"
              className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-full bg-ink/50 text-white backdrop-blur-sm hover:bg-ink/70"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}
        {/* Extra-photo indicator, Instagram-carousel style — lives ON the
            image as an overlay, so it never adds or removes row height.
            Only rendered at all when a product actually has extra photos
            (Pro-only field), so this never affects card height uniformity
            regardless of plan or per-product photo count. */}
        {gallery.length > 1 && (
          <div className="absolute inset-x-0 bottom-2.5 flex justify-center gap-1.5">
            {gallery.map((url, i) => (
              <button
                key={url + i}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImage(i);
                }}
                aria-label={`Photo ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === activeImage ? "w-4 bg-white" : "w-1.5 bg-white/60"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Category tag row — fixed height so every card in a grid row lines
          up identically, whether or not this product has a category. When
          a product has no category, the row stays reserved (via
          `invisible`) but empty, same principle as the old thumbnail-strip
          fix — the difference is that now, whenever there IS a category,
          the space is doing something useful instead of sitting blank. */}
      <div
        className={`flex h-9 items-center gap-1.5 border-b border-ink/5 bg-ink/[0.02] px-3 ${
          product.category ? "" : "invisible"
        }`}
      >
        {product.category && (
          <span className="inline-flex items-center gap-1 rounded-full bg-hotpink/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-hotpink">
            <Tag size={10} /> {product.category}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        {productHref ? (
          <Link to={productHref} className="line-clamp-2 text-sm font-bold text-ink hover:text-hotpink">
            {product.name}
          </Link>
        ) : (
          <p className="line-clamp-2 text-sm font-bold text-ink">{product.name}</p>
        )}
        <p className="mt-1 line-clamp-2 min-h-[2.25rem] text-xs text-ink/50">{product.description || ""}</p>
        <div className="mt-3 flex min-h-[1.75rem] flex-wrap items-center gap-1.5">
          {availableSizes.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSelectedSize(s)}
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition ${
                selectedSize === s ? "bg-ink text-cream" : "border border-ink/15 bg-white text-ink/60 hover:bg-ink/5"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-ink">
            {Number(product.price) === 0 ? "Free" : `${symbol}${Number(product.price)}`}
          </span>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-2 text-xs font-semibold text-ink hover:bg-ink/5"
            >
              {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
            </button>
            {isLinkOnly ? (
              <a
                href={buyUrl}
                target="_blank"
                rel="sponsored noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-3.5 py-2 text-xs font-semibold text-white hover:opacity-95"
              >
                {buyLabel} <ArrowUpRight size={13} />
              </a>
            ) : (
              <button
                type="button"
                onClick={() => onAddToCart(selectedSize)}
                disabled={isOutOfStock}
                className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-3.5 py-2 text-xs font-semibold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ShoppingCart size={13} /> {isOutOfStock ? "Sold out" : "Add"}
              </button>
            )}
          </div>
        </div>
        {/* Normal product that ALSO has a Buy link (DM shows Select + Shop now): full-width Shop now under the row */}
        {buyUrl && !isLinkOnly && (
          <a
            href={buyUrl}
            target="_blank"
            rel="sponsored noopener noreferrer"
            className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-ink/15 bg-white px-3.5 py-2 text-xs font-semibold text-ink hover:bg-ink/5"
          >
            {buyLabel} <ArrowUpRight size={13} />
          </a>
        )}
      </div>
    </div>
  );
}
